/**
 * WS-HF shared login — Admin → AI providers → Higgsfield account → **Company subscription**.
 *
 * The owner signs the company in to Higgsfield ONCE, from here. Every designer's cloud case job
 * then runs on that one subscription; a designer never sees or sets up Higgsfield (docs/
 * higgsfield-mcp.md §"The shared company connection"). SUPER-ADMIN ONLY — every route this
 * block calls answers a tenant admin 403, and `ProvidersTab` renders the read-only card for
 * them instead.
 *
 * The sign-in is out-of-band: `POST …/login` answers the page to open, the page opens OUTSIDE
 * the app (`io.openExternal` — the system browser on the desktop, a new tab on the web), and the
 * browser returns to the CLOUD's callback, never to this client. So this block only polls
 * `GET …/status` until the cloud says connected, and the admin can stop waiting at any time.
 * Nothing about the grant — no token, no code — passes through here: the widest thing the
 * status carries is a credit balance.
 *
 * Disconnect and Re-attach both change what every designer's next job does, so both ask first
 * through `ConfirmPopover` — never `window.confirm`, which is absent under Tauri and would then
 * answer "yes" for nobody.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useServices } from "../app/context.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { Icon } from "../ui/Icon.js";
import { InfoHint } from "../ui/Tooltip.js";
import type { HiggsfieldSubscriptionStatus } from "@genz/platform";

/** How often the card asks the cloud whether the sign-in finished. */
const POLL_MS = 3000;
/** The cloud keeps a sign-in open for 10 minutes; used when `expires_in` is absent. */
const DEFAULT_LOGIN_SECS = 600;

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** `2026-09-24 08:15 UTC` — a readable, locale-stable instant. */
function when(iso: string | null | undefined): string {
  if (!iso) return "an unknown time";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

/** Credits, to at most two places — the vendor bills 1.5 for a Nano Banana. */
function credits(n: number): string {
  return String(Math.round(n * 100) / 100);
}

export function HiggsfieldCompanyConnection({
  /** Injectable for tests. */
  pollMs = POLL_MS,
  /** Called with every status read, so the page can show which wallet is in force. */
  onStatus,
}: {
  pollMs?: number;
  onStatus?: (status: HiggsfieldSubscriptionStatus) => void;
} = {}) {
  const { client, io } = useServices();
  const [status, setStatus] = useState<HiggsfieldSubscriptionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The sign-in in flight: the page to (re)open and when the cloud stops accepting it. */
  const [waiting, setWaiting] = useState<{ url: string; until: number } | null>(null);
  const [reattached, setReattached] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const alive = useRef(true);

  const stopPolling = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  const read = useCallback(async (): Promise<HiggsfieldSubscriptionStatus | null> => {
    try {
      const s = await client.higgsfieldSubscriptionStatus();
      if (!alive.current) return null;
      setStatus(s);
      onStatus?.(s);
      return s;
    } catch (e) {
      if (alive.current) setError(errText(e));
      return null;
    }
  }, [client, onStatus]);

  useEffect(() => {
    alive.current = true;
    void read();
    // A timer that outlived the card would keep asking the cloud after the admin left.
    return () => { alive.current = false; stopPolling(); };
  }, [read, stopPolling]);

  /** One poll tick; re-arms itself until connected, stopped, or the sign-in expired. */
  const tick = useCallback((until: number) => {
    timer.current = setTimeout(() => {
      void (async () => {
        const s = await read();
        if (!alive.current || timer.current === undefined) return;
        if (s?.connected) {
          timer.current = undefined;
          setWaiting(null);
          setError(null);
          return;
        }
        if (Date.now() >= until) {
          timer.current = undefined;
          setWaiting(null);
          setError("The sign-in was not finished in time. Connect again to start a new one.");
          return;
        }
        tick(until);
      })();
    }, pollMs);
  }, [pollMs, read]);

  async function open(url: string) {
    if (!io.openExternal) {
      setError("This app cannot open a browser here. Update the app, or connect from the web app.");
      return;
    }
    try {
      await io.openExternal(url);
    } catch (e) {
      setError(`The sign-in page did not open (${errText(e)}). Press "Open the sign-in page" to try again.`);
    }
  }

  async function connect() {
    setError(null);
    setReattached(null);
    setBusy(true);
    try {
      const start = await client.higgsfieldSubscriptionLogin();
      const until = Date.now() + (start.expires_in ?? DEFAULT_LOGIN_SECS) * 1000;
      setWaiting({ url: start.authorize_url, until });
      stopPolling();
      tick(until);
      await open(start.authorize_url);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  function stopWaiting() {
    stopPolling();
    setWaiting(null);
  }

  async function disconnect() {
    setError(null);
    setReattached(null);
    setBusy(true);
    try {
      const s = await client.higgsfieldSubscriptionDisconnect();
      setStatus(s);
      onStatus?.(s);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  async function reattach() {
    setError(null);
    setBusy(true);
    try {
      const r = await client.higgsfieldSubscriptionReattach();
      setReattached(r.reattached);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  const connected = !!status?.connected;
  /* EXPIRED is "not connected, and the server says why": a dead grant or a 401 that survived
     the refresh. The words are the server's, never a guess made here. */
  const expired = !!status && !status.connected && !!status.error;
  const who = status?.connected_by_email || status?.connected_by || "an admin";
  const stateWord = status === null ? "Reading…" : connected ? "Connected" : expired ? "Expired" : "Not connected";

  return (
    <div className="hf-company" data-testid="hf-company">
      <h3 className="admin-section-title">
        Company subscription{" "}
        <InfoHint text="Sign in to Higgsfield once, here. Every designer's image generation then runs on this one subscription. Designers never see or set up Higgsfield." />
      </h3>

      {error && <p className="banner banner--danger" role="alert" data-testid="hf-company-error">{error}</p>}

      <div className="setting-row">
        <div>
          <span className="setting-row__label">Status</span>
          <span className="setting-row__hint">One sign-in for every designer</span>
        </div>
        <div className="setting-row__control">
          <span
            className={`badge ${connected ? "badge--success" : expired ? "badge--warn" : "badge--neutral"}`}
            data-testid="hf-company-status"
          >
            {stateWord}
          </span>
        </div>
      </div>

      {expired && (
        <p className="banner banner--warn" role="status" data-testid="hf-company-expired">
          {status!.error}
          {status!.connected_at ? ` Last connected by ${who} on ${when(status!.connected_at)}.` : ""}
        </p>
      )}

      {connected && (
        <div data-testid="hf-company-details">
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Plan and balance</span>
              <span className="setting-row__hint">Read from Higgsfield now</span>
            </div>
            <div className="setting-row__control">
              <span data-testid="hf-company-balance">
                {status!.plan ? `${status!.plan} plan` : "Plan unknown"}
                {" · "}
                {status!.credits != null ? `${credits(status!.credits)} credits` : "balance unavailable"}
              </span>
            </div>
          </div>
          {status!.error && (
            /* Connected, but the balance read failed: the account still works, so this is a
               quiet line and not a banner — the connection is not in doubt. */
            <span className="caption" data-testid="hf-company-balance-note">{status!.error}</span>
          )}
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Connected by</span>
              <span className="setting-row__hint">Who signed the company in</span>
            </div>
            <div className="setting-row__control">
              <span data-testid="hf-company-connected-by">{who} · {when(status!.connected_at)}</span>
            </div>
          </div>
        </div>
      )}

      {waiting ? (
        <div className="setting-row" data-testid="hf-company-waiting" role="status" aria-live="polite">
          <div>
            <span className="setting-row__label">Waiting for the sign-in</span>
            <span className="setting-row__hint">Finish signing in to Higgsfield in the browser. This page checks every few seconds.</span>
            {/* A popup blocker stops the new tab SILENTLY: with `noopener` the web gets no
                handle back to test. So the row always says what to do (fix round, item 4). */}
            <span className="setting-row__hint" data-testid="hf-company-no-tab">No tab opened? Press Open the sign-in page.</span>
          </div>
          <div className="setting-row__control">
            <button type="button" className="btn btn--sm" onClick={() => void open(waiting.url)} data-testid="hf-company-open-again">
              <Icon name="arrow-right" size={16} /> Open the sign-in page
            </button>
            <button type="button" className="btn btn--sm btn--ghost" onClick={stopWaiting} data-testid="hf-company-stop">
              Stop waiting
            </button>
          </div>
        </div>
      ) : connected ? (
        <>
          <div className="setting-row">
            <div>
              <span className="setting-row__label">Disconnect</span>
              <span className="setting-row__hint">Designers&rsquo; new jobs stop using the subscription</span>
            </div>
            <div className="setting-row__control">
              <ConfirmPopover
                message="Disconnect the company Higgsfield subscription? Every designer's next job stops using it: on Automatic it moves to the API key, otherwise it cannot start until you connect again."
                confirmLabel="Disconnect"
                danger
                onConfirm={() => void disconnect()}
              >
                {(toggle) => (
                  <button type="button" className="btn btn--sm" disabled={busy} onClick={toggle} data-testid="hf-company-disconnect">
                    Disconnect
                  </button>
                )}
              </ConfirmPopover>
            </div>
          </div>
          <div className="setting-row" data-testid="hf-company-reattach-row">
            <div>
              <span className="setting-row__label">Re-attach held jobs</span>
              <span className="setting-row__hint">
                Only after you connected the same Higgsfield account again. Held jobs from the earlier
                connection are then checked on this one, so nothing is paid for twice.
              </span>
            </div>
            <div className="setting-row__control">
              <ConfirmPopover
                message="Re-attach held jobs to this account? Do this only if it is the same Higgsfield account as before. A different account cannot find those jobs."
                confirmLabel="Re-attach"
                onConfirm={() => void reattach()}
              >
                {(toggle) => (
                  <button type="button" className="btn btn--sm" disabled={busy} onClick={toggle} data-testid="hf-company-reattach">
                    Re-attach
                  </button>
                )}
              </ConfirmPopover>
              {reattached !== null && (
                <span className="faint" role="status" data-testid="hf-company-reattached">
                  {reattached === 1 ? "1 held job re-attached." : `${reattached} held jobs re-attached.`}
                </span>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="setting-row">
          <div>
            <span className="setting-row__label">Sign in</span>
            <span className="setting-row__hint">Opens Higgsfield outside the app</span>
          </div>
          <div className="setting-row__control">
            <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void connect()} data-testid="hf-company-connect">
              Connect Higgsfield (company subscription)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * WS-HF shared login — the same card for a TENANT admin, read-only.
 *
 * Every `…/higgsfield/mcp/*` route answers a tenant admin 403, and the connection is
 * platform-wide: a tenant admin who could replace or disconnect it could stop every designer's
 * work. So this reads only what `GET /api/customization/capabilities` tells ANY signed-in caller
 * — which wallet a designer's job spends and whether it is ready — and has no control at all.
 */
export function HiggsfieldCompanyReadOnly() {
  const { client } = useServices();
  const [caps, setCaps] = useState<{ source?: "subscription" | "api"; ready?: boolean } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const c = await client.customizationCapabilities?.();
        if (!cancelled) setCaps({ source: c?.shared_generation_source, ready: c?.shared_generation_connected });
      } catch {
        if (!cancelled) setCaps({});
      }
    })();
    return () => { cancelled = true; };
  }, [client]);

  const wallet = caps?.source === "api" ? "the platform's Higgsfield API key" : "the company Higgsfield subscription";
  const line = caps === null
    ? "Reading…"
    : caps.ready === undefined
      ? "The image-generation status is not available from this server."
      : caps.ready
        ? `Ready. Designers' images run on ${wallet}.`
        : "Image generation is not set up yet.";

  return (
    <div className="admin-panel" data-testid="hf-company-readonly">
      <div className="admin-panel__head">
        <div>
          <strong className="admin-panel__title">Higgsfield account</strong>
          <p className="admin-panel__sub">The company Higgsfield subscription every designer generates on.</p>
        </div>
      </div>
      <div className="setting-row">
        <div>
          <span className="setting-row__label">Image generation</span>
          <span className="setting-row__hint">Managed by a super-admin</span>
        </div>
        <div className="setting-row__control">
          <span data-testid="hf-company-readonly-state">{line}</span>
        </div>
      </div>
      <p className="caption" data-testid="providers-requires-super">Only a super-admin can change this.</p>
    </div>
  );
}
